import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GetIsochroneVersionListResponseType } from 'features/Location/services/isochroneVersioning'

type IsochroneVersionItemType = GetIsochroneVersionListResponseType['data']['list'][number]

type PaginationType = GetIsochroneVersionListResponseType['pagination']

type QueryType = {
  limit: number
  cursor: string
  direction: string
}

type InitialStateType = {
  isLoading: boolean
  query: QueryType
  currentPage: number
  isochroneVersionList: IsochroneVersionItemType[]
  pagination: PaginationType
  activeVersion: string
  selectedIds: string[]
  versionIdToDelete: string | null
  isBulkDeleteDialogOpen: boolean
}

export const SLICE_NAME = 'isochroneVersioning'

const initialPagination: PaginationType = {
  next_page_exist: false,
  previous_page_exist: false,
  page_size: 0,
  page_cursor: '',
  cursor_direction: '',
  page_item: 0,
  previous_cursor: '',
  next_cursor: '',
}

export const initialState: InitialStateType = {
  isLoading: false,
  query: { limit: 20, cursor: '', direction: '' },
  currentPage: 1,
  isochroneVersionList: [],
  pagination: initialPagination,
  activeVersion: '',
  selectedIds: [],
  versionIdToDelete: null,
  isBulkDeleteDialogOpen: false,
}

const isochroneVersioning = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetState: () => initialState,
    setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload
    },
    setQuery: (state, { payload }: PayloadAction<Partial<QueryType>>) => {
      state.query = { ...state.query, ...payload }
    },
    setIsochroneVersionList: (state, { payload }: PayloadAction<IsochroneVersionItemType[]>) => {
      state.isochroneVersionList = payload
    },
    setPagination: (state, { payload }: PayloadAction<PaginationType>) => {
      state.pagination = payload
    },
    setActiveVersion: (state, { payload }: PayloadAction<string>) => {
      state.activeVersion = payload
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload
    },
    toggleSelectedId: (state, { payload }: PayloadAction<string>) => {
      const idx = state.selectedIds.indexOf(payload)
      if (idx === -1) {
        state.selectedIds.push(payload)
      } else {
        state.selectedIds.splice(idx, 1)
      }
    },
    setSelectedIds: (state, { payload }: PayloadAction<string[]>) => {
      state.selectedIds = payload
    },
    resetSelectedIds: (state) => {
      state.selectedIds = []
    },
    setVersionIdToDelete: (state, { payload }: PayloadAction<string | null>) => {
      state.versionIdToDelete = payload
    },
    setIsBulkDeleteDialogOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.isBulkDeleteDialogOpen = payload
    },
  },
})

export default isochroneVersioning.reducer
export const {
  resetState,
  setIsLoading,
  setQuery,
  setIsochroneVersionList,
  setPagination,
  setActiveVersion,
  setCurrentPage,
  toggleSelectedId,
  setSelectedIds,
  resetSelectedIds,
  setVersionIdToDelete,
  setIsBulkDeleteDialogOpen,
} = isochroneVersioning.actions
